import React, { useState } from 'react';
import { X, Brain, Network, AlertTriangle, CheckCircle, Loader, Target, ArrowRight } from 'lucide-react';
import PromptDebugger from '../Debug/PromptDebugger';
import { apiService } from '../../services/api';
import { apiConfig } from '../../config/api.config';

interface DependencySuggestion {
  sourceGoalId: string;
  targetGoalId: string;
  sourceGoalName: string;
  targetGoalName: string;
  dependencyType: 'blocks' | 'enables' | 'reduces_complexity' | 'accelerates';
  strength: number;
  reasoning: string;
  impact: 'high' | 'medium' | 'low';
}

interface RiskFactor {
  risk: string;
  affectedGoals: string[];
  mitigation: string;
}

interface DependencyAnalysis {
  suggestedDependencies: DependencySuggestion[];
  recommendations: string[];
  riskFactors: RiskFactor[];
  ai_generated: boolean;
  generated_at: string;
  debug?: any;
}

interface AIDependencyAnalyzerProps {
  projectId: string;
  onClose: () => void;
  onDependenciesCreated: () => void;
}

const AIDependencyAnalyzer: React.FC<AIDependencyAnalyzerProps> = ({
  projectId,
  onClose,
  onDependenciesCreated
}) => {
  const [analysis, setAnalysis] = useState<DependencyAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [selectedDependencies, setSelectedDependencies] = useState<Set<number>>(new Set());
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError('');
    setAnalysis(null);

    try {
      const response = await fetch(apiConfig.endpoints.ai.analyzeDependencies, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('projectai_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Analysis failed');
      }

      const data = await response.json();
      setAnalysis(data.analysis);
      setDebugInfo(data.debug);

      // Pre-select high impact dependencies
      const highImpactIndices = data.analysis.suggestedDependencies
        .map((dep: DependencySuggestion, index: number) => ({ dep, index }))
        .filter(({ dep }: { dep: DependencySuggestion; index: number }) => dep.impact === 'high')
        .map(({ index }: { dep: DependencySuggestion; index: number }) => index);

      setSelectedDependencies(new Set(highImpactIndices));

    } catch (err: any) {
      console.error('AI dependency analysis failed:', err);
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDependencies = async () => {
    if (!analysis || selectedDependencies.size === 0) return;

    setCreating(true);
    setError('');

    try {
      const results = [];
      const errors = [];

      // Process dependencies one by one to handle conflicts properly
      for (const index of Array.from(selectedDependencies)) {
        const dep = analysis.suggestedDependencies[index];

        try {
          const result = await apiService.createDependency({
            sourceGoalId: dep.sourceGoalId,
            targetGoalId: dep.targetGoalId,
            dependencyType: dep.dependencyType,
            strength: dep.strength,
            description: dep.reasoning
          });
          results.push(result);
        } catch (err: any) {
          console.error(`Failed to create dependency between ${dep.sourceGoalName} → ${dep.targetGoalName}:`, err);

          // Handle specific conflict errors
          if (err.response?.status === 409) {
            errors.push(`Dependency already exists: ${dep.sourceGoalName} → ${dep.targetGoalName}`);
          } else if (err.response?.status === 400) {
            errors.push(`Invalid dependency: ${dep.sourceGoalName} → ${dep.targetGoalName} (${err.response.data.error || 'Bad request'})`);
          } else {
            errors.push(`Failed to create: ${dep.sourceGoalName} → ${dep.targetGoalName}`);
          }
        }
      }

      // Show success message with any warnings
      if (results.length > 0) {
        if (errors.length > 0) {
          setError(`Created ${results.length} dependencies. Issues: ${errors.join('; ')}`);
        }
        onDependenciesCreated();
      } else if (errors.length > 0) {
        setError(`Failed to create dependencies: ${errors.join('; ')}`);
      }

    } catch (err: any) {
      console.error('Failed to create dependencies:', err);
      setError('Failed to create dependencies');
    } finally {
      setCreating(false);
    }
  };

  const toggleDependencySelection = (index: number) => {
    const newSelected = new Set(selectedDependencies);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedDependencies(newSelected);
  };

  const getDependencyTypeColor = (type: string) => {
    switch (type) {
      case 'blocks': return 'bg-red-100 text-red-800 border-red-200';
      case 'enables': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'reduces_complexity': return 'bg-green-100 text-green-800 border-green-200';
      case 'accelerates': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength >= 8) return 'text-red-600';
    if (strength >= 6) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-indigo-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  AI Dependency Analyzer
                </h2>
                <p className="text-sm text-gray-600">
                  Automatically detect logical dependencies between goals
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            <div className="p-6">
              {!analysis && !loading && (
                <div className="text-center py-12">
                  <Network className="mx-auto w-16 h-16 text-indigo-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Ready to Analyze Dependencies
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    AI will analyze all goals in your project and suggest logical dependencies
                    based on content, complexity, and implementation requirements.
                  </p>
                  <button
                    onClick={handleAnalyze}
                    className="btn-primary flex items-center gap-2 mx-auto"
                  >
                    <Brain className="w-4 h-4" />
                    Analyze Project Dependencies
                  </button>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <Loader className="mx-auto w-8 h-8 text-indigo-600 animate-spin mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Analyzing Dependencies...
                  </h3>
                  <p className="text-gray-600">
                    AI is examining goal relationships and identifying logical dependencies.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <h3 className="font-medium text-red-900">Analysis Failed</h3>
                  </div>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              )}

              {analysis && (
                <div className="space-y-6">
                  {/* Analysis Summary */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-medium text-indigo-900">Analysis Complete</h3>
                      {analysis.ai_generated && (
                        <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                          AI Generated
                        </span>
                      )}
                    </div>
                    <p className="text-indigo-800 text-sm">
                      Found {analysis.suggestedDependencies.length} potential dependencies.
                      Select the ones you want to create.
                    </p>
                  </div>

                  {/* Suggested Dependencies */}
                  {analysis.suggestedDependencies.length > 0 ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Suggested Dependencies
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          (Select to create)
                        </span>
                      </h3>

                      <div className="space-y-3">
                        {analysis.suggestedDependencies.map((dep, index) => (
                          <div
                            key={index}
                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                              selectedDependencies.has(index)
                                ? 'border-indigo-300 bg-indigo-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => toggleDependencySelection(index)}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedDependencies.has(index)}
                                onChange={() => toggleDependencySelection(index)}
                                className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="flex items-center gap-2">
                                    <Target className="w-4 h-4 text-gray-400" />
                                    <span className="font-medium text-gray-900">
                                      {dep.sourceGoalName}
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                    <span className="font-medium text-gray-900">
                                      {dep.targetGoalName}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-3 mb-3">
                                  <span className={`px-2 py-1 text-xs font-medium border rounded-full ${getDependencyTypeColor(dep.dependencyType)}`}>
                                    {dep.dependencyType.toUpperCase()}
                                  </span>
                                  <span className={`text-sm font-medium ${getStrengthColor(dep.strength)}`}>
                                    Strength: {dep.strength}/10
                                  </span>
                                  <span className={`text-sm font-medium ${getImpactColor(dep.impact)}`}>
                                    Impact: {dep.impact}
                                  </span>
                                </div>

                                <p className="text-gray-700 text-sm mb-2">
                                  {dep.reasoning}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Network className="mx-auto w-12 h-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No New Dependencies Found
                      </h3>
                      <p className="text-gray-600">
                        Your project goals appear to be well-structured with existing dependencies,
                        or the goals are independent of each other.
                      </p>
                    </div>
                  )}

                  {/* Recommendations */}
                  {analysis.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Recommendations
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <ul className="space-y-2">
                          {analysis.recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2 text-blue-800">
                              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                              <span className="text-sm">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Risk Factors */}
                  {analysis.riskFactors.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Risk Factors
                      </h3>
                      <div className="space-y-3">
                        {analysis.riskFactors.map((risk, index) => (
                          <div key={index} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                              <div className="flex-1">
                                <h4 className="font-medium text-amber-900 mb-1">{risk.risk}</h4>
                                <p className="text-amber-800 text-sm mb-2">
                                  <strong>Affected Goals:</strong> {risk.affectedGoals.join(', ')}
                                </p>
                                <p className="text-amber-800 text-sm">
                                  <strong>Mitigation:</strong> {risk.mitigation}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Debug Information */}
            {debugInfo && (
              <div className="px-6 pb-6">
                <PromptDebugger
                  title="AI Dependency Analysis Prompt"
                  prompt={debugInfo.prompt}
                  response={debugInfo.rawResponse}
                  metadata={debugInfo.metadata}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          {analysis && analysis.suggestedDependencies.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 min-h-[80px]">
              <div className="text-sm text-gray-600">
                {selectedDependencies.size} of {analysis.suggestedDependencies.length} dependencies selected
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleAnalyze()}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Re-analyze
                </button>
                <button
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDependencies}
                  disabled={selectedDependencies.size === 0 || creating}
                  className="btn-primary flex items-center gap-2"
                >
                  {creating ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Network className="w-4 h-4" />
                  )}
                  Create {selectedDependencies.size} Dependencies
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIDependencyAnalyzer;
